import React, { useEffect, useMemo, useState } from "react";
import PlayerBasicCard from "./PlayerBasicCard.jsx";
import { useTheme } from "../common/ThemeContext.jsx";
import { usePlayersPerf,normalizePlayer  } from "../common/PlayersPerfContext.jsx";
import { useSeason } from "../common/SeasonContext.jsx";
import { fetchAuth } from "../common/api.jsx";
import { ClipboardList, X, Trash2, Check } from "lucide-react";
import { encodeId } from "../common/hashid.js";
import { useTranslation } from "react-i18next";

function isoToday() {
  // YYYY-MM-DD
  return new Date().toISOString().slice(0, 10);
}
async function togglePlayer(playerId, enable, onRefetchGlobal) {
  try {
    const encodedId = encodeId(playerId);
    const res = await fetchAuth(`/players/${encodedId}/toggle_enabled`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ player_enabled: enable }),
    });

    if (!res.ok) throw new Error("Impossible de modifier le statut.");

    const data = await res.json();
    
    
    // Trigger global refetch to sync all components
    if (onRefetchGlobal) {
      await onRefetchGlobal();
    }
    
    return data.player_enabled;
  } catch (e) {
    alert(e.message);
    return null;
  }
}
function typeLabel(t) {
  if (t === "douleur") return "Douleur";
  if (t === "musculaire") return "Musculaire";
  if (t === "articulaire") return "Articulaire";
  return "Autre";
}
function typeChipCls(t, isDark) {
  const base = "inline-flex items-center px-2 py-0.5 rounded-full text-xs border";
  if (t === "douleur")     return `${base} ${isDark ? "bg-orange-500/10 border-orange-400/40 text-orange-200" : "bg-orange-50 border-orange-200 text-orange-700"}`;
  if (t === "musculaire")  return `${base} ${isDark ? "bg-red-500/10 border-red-400/40 text-red-200" : "bg-red-50 border-red-200 text-red-700"}`;
  if (t === "articulaire") return `${base} ${isDark ? "bg-amber-500/10 border-amber-400/40 text-amber-200" : "bg-amber-50 border-amber-200 text-amber-700"}`;
  return `${base} ${isDark ? "bg-slate-500/10 border-slate-400/40 text-slate-200" : "bg-slate-50 border-slate-200 text-slate-700"}`;
}

function MiniPlayerRow({ player, isDark }) {
  const initials = `${player.first_name?.[0] ?? ""}${player.last_name?.[0] ?? ""}`.toUpperCase();

  return (
    <div className="flex items-center gap-3">
      {/* Avatar simple (si tu as une url photo, remplace par <img />) */}
      <div className={`h-9 w-9 rounded-full flex items-center justify-center text-xs font-semibold ring-1 ${
        isDark ? "bg-gray-800 ring-white/10 text-gray-100" : "bg-white ring-black/10 text-gray-800"
      }`}>
        {initials || "?"}
      </div>

      <div className="min-w-0">
        <div className="font-medium truncate">
          {player.first_name} {player.last_name}
        </div>
        <div className={`text-xs ${isDark ? "text-gray-400" : "text-gray-500"}`}>
          {player.position || "N/A"}
        </div>
      </div>
    </div>
  );
}

function ContractDrawerContent({ displayedPlayers, isDarkMode,contractsByPlayerId,showToast , activePlayerId,
  setContractsByPlayerId }) {
  const [selectedPlayerId, setSelectedPlayerId] = useState(activePlayerId || "");

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Récupérer les dates depuis la DB
useEffect(() => {
  if (!selectedPlayerId) {
    setStartDate("");
    setEndDate("");
    return;
  }
  

  async function fetchContract() {
    try {
      const encodedId = encodeId(selectedPlayerId);

      const res = await fetchAuth(
        `/contracts/${encodedId}`
      );

      if (!res.ok) throw new Error("Failed to fetch contract");

      const data = await res.json();

      // ✅ IMPORTANT : toujours forcer format correct
      setStartDate(
        data.start_date ? data.start_date.slice(0, 10) : ""
      );

      setEndDate(
        data.end_date ? data.end_date.slice(0, 10) : ""
      );

    } catch (e) {
      
      setStartDate("");
      setEndDate("");
    }
  }

  fetchContract();
}, [selectedPlayerId]);

useEffect(() => {
  if (activePlayerId) {
    setSelectedPlayerId(activePlayerId);
  }
}, [activePlayerId]);

async function updateContract() {
  if (!selectedPlayerId) return;
  try {
    const res = await fetchAuth(`/contracts/${selectedPlayerId}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ start_date: startDate, end_date: endDate }),
    });
    if (!res.ok) throw new Error("Update failed");

    // 🔥 Mettre à jour le state local immédiatement
setContractsByPlayerId(prev => ({
  ...prev,
  [selectedPlayerId]: {
    ...prev[selectedPlayerId],
    start_date: startDate,
    end_date: endDate
  }
}));

    showToast("success", "Contract updated successfully.");
  } catch (e) {
    showToast("error", "Failed to update contract.");
   
  }
}

  return (
    <div className={`mt-5 rounded-2xl border p-4 shadow-sm ${isDarkMode ? "border-white/10 bg-gray-900/40" : "border-black/10 bg-white"}`}>
      <div className="font-semibold">Modify Player Contract</div>
      <div className={`mt-1 text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
        Select a player and update contract dates.
      </div>

      <div className="mt-3 space-y-3">
        <div>
          <div className={`text-xs mb-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>Player</div>
          <select
            value={selectedPlayerId}
            onChange={(e) => setSelectedPlayerId(e.target.value)}
            disabled={!!activePlayerId} 
            className={`w-full rounded-xl border px-3 py-2.5 text-sm shadow-sm ${isDarkMode ? "bg-gray-950 border-gray-700" : "bg-white border-gray-300"}`}
          >
            <option value="">— Select —</option>
            {displayedPlayers.map((p) => (
              <option key={p.id} value={p.id}>
                {p.first_name} {p.last_name} ({p.position || "N/A"})
              </option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <div className={`text-xs mb-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>Start Date</div>
<input
  type="date"
  value={startDate}
  onChange={(e) => setStartDate(e.target.value)}
  className={`w-full rounded-xl border px-3 py-2.5 text-sm shadow-sm ${isDarkMode ? "bg-gray-950 border-gray-700" : "bg-white border-gray-300"}`}
/>
          </div>
          <div>
            <div className={`text-xs mb-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>End Date</div>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className={`w-full rounded-xl border px-3 py-2.5 text-sm shadow-sm ${isDarkMode ? "bg-gray-950 border-gray-700" : "bg-white border-gray-300"}`}
            />
          </div>
        </div>

        <button
          onClick={updateContract}
          disabled={!selectedPlayerId}
          className={`w-full rounded-xl px-3 py-2.5 text-sm font-semibold transition shadow-sm ${
            !selectedPlayerId
              ? (isDarkMode ? "bg-gray-800 text-gray-400" : "bg-gray-200 text-gray-500")
              : (isDarkMode ? "bg-green-600 hover:bg-green-500 text-white" : "bg-green-600 hover:bg-green-700 text-white")
          }`}
        >
          Update Contract
        </button>
      </div>
    </div>
  );
}
function AbsenceRow({ a, player, isDarkMode, fetchAbsences, activeOn, showToast, deleteAbsence, setHasUnsavedChanges }) {
  const [startDate, setStartDate] = useState(a.start_date?.slice(0, 10) || "");
  const [endDate, setEndDate] = useState(a.end_date?.slice(0, 10) || "");
  const [absType, setAbsType] = useState(a.absence_type);
  const [isDirty, setIsDirty] = useState(false);
  const [isSaved, setIsSaved] = useState(false);

  const inputCls = `rounded-lg border px-2 py-1 text-sm w-36 ${isDarkMode ? "bg-gray-800 border-gray-600 text-gray-100 [color-scheme:dark]" : "bg-white border-gray-300 [color-scheme:light]"}`;

  async function validate() {
    try {
      const res = await fetchAuth(`/absences/${a.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ absence_type: absType, start_date: startDate, end_date: endDate }),
      });
      if (!res.ok) throw new Error();
      await fetchAbsences(activeOn);
      showToast("success", "Absence updated.");
      setHasUnsavedChanges(false);
      setIsDirty(false);
      setIsSaved(true);
      setTimeout(() => setIsSaved(false), 2000);
    } catch { showToast("error", "Failed to update."); }
  }

  return (
    <tr className={isDarkMode ? "bg-gray-900" : "bg-white"}>
      <td className="px-3 py-2 border-b">{player.first_name}</td>
      <td className="px-3 py-2 border-b">{player.last_name}</td>
      <td className="px-3 py-2 border-b">{player.position}</td>
      <td className="px-3 py-2 border-b">
        <input type="date" value={startDate}
          onChange={e => { setStartDate(e.target.value); setIsDirty(true); setIsSaved(false); setHasUnsavedChanges(true); }}
          className={inputCls} />
      </td>
      <td className="px-3 py-2 border-b">
        <input type="date" value={endDate}
          onChange={e => { setEndDate(e.target.value); setIsDirty(true); setIsSaved(false); setHasUnsavedChanges(true); }}
          className={inputCls} />
      </td>
      <td className="px-3 py-2 border-b">
        <select value={absType}
          onChange={e => { setAbsType(e.target.value); setIsDirty(true); setIsSaved(false); setHasUnsavedChanges(true); }}
          className={`rounded-lg border px-2 py-1 text-sm ${isDarkMode ? "bg-gray-800 border-gray-600 text-gray-100" : "bg-white border-gray-300"}`}>
          <option value="douleur">Douleur</option>
          <option value="musculaire">Musculaire</option>
          <option value="articulaire">Articulaire</option>
          <option value="other">Autre</option>
        </select>
      </td>
      <td className="px-3 py-2 border-b">
        <div className="flex gap-2">
          {/* ✅ Bouton validate — visible seulement si modifié ou juste sauvegardé */}
          {(isDirty || isSaved) && (
            <button onClick={validate}
              className={`p-1.5 rounded-lg transition ${
                isSaved
                  ? "bg-green-500/20 border border-green-400/30 text-green-400"
                  : isDarkMode
                    ? "bg-indigo-500/20 border border-indigo-400/30 text-indigo-300 hover:bg-indigo-500/30"
                    : "bg-indigo-50 border border-indigo-200 text-indigo-700 hover:bg-indigo-100"
              }`}
              title="Validate">
              <Check className="h-4 w-4" />
            </button>
          )}
          <button onClick={() => deleteAbsence(a.id)}
            className={`p-1.5 rounded-lg transition ${isDarkMode ? "bg-red-500/20 border border-red-400/30 text-red-400 hover:bg-red-500/30" : "bg-red-50 border border-red-200 text-red-600 hover:bg-red-100"}`}
            title="Delete">
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </td>
    </tr>
  );
}
function PlayerDropdown({ displayedPlayers, selectedPlayerId, setSelectedPlayerId, isDarkMode }) {
  const [open, setOpen] = useState(false);
  const selected = displayedPlayers.find(p => p.id === selectedPlayerId);

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className={`w-full rounded-xl border px-3 py-2.5 text-sm shadow-sm text-left flex items-center justify-between ${
          isDarkMode ? "bg-gray-950 border-gray-700 text-gray-100" : "bg-white border-gray-300 text-gray-900"
        }`}
      >
        <span className={selected ? "" : (isDarkMode ? "text-gray-500" : "text-gray-400")}>
          {selected ? `${selected.first_name} ${selected.last_name} (${selected.position || "N/A"})` : "— Select —"}
        </span>
        <svg className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""} ${isDarkMode ? "text-gray-400" : "text-gray-500"}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div className={`absolute left-0 top-full mt-1 z-50 w-full rounded-xl border shadow-lg overflow-y-auto max-h-48 ${
          isDarkMode ? "bg-gray-950 border-gray-700" : "bg-white border-gray-200"
        }`}>
          <div
            onClick={() => { setSelectedPlayerId(""); setOpen(false); }}
            className={`px-3 py-2 text-sm cursor-pointer ${isDarkMode ? "text-gray-500 hover:bg-gray-800" : "text-gray-400 hover:bg-gray-50"}`}
          >
            — Select —
          </div>
          {displayedPlayers.map(p => (
            <div
              key={p.id}
              onClick={() => { setSelectedPlayerId(p.id); setOpen(false); }}
              className={`px-3 py-2 text-sm cursor-pointer ${
                selectedPlayerId === p.id
                  ? (isDarkMode ? "bg-indigo-600/30 text-indigo-200" : "bg-indigo-50 text-indigo-700")
                  : (isDarkMode ? "text-gray-100 hover:bg-gray-800" : "text-gray-900 hover:bg-gray-50")
              }`}
            >
              {p.first_name} {p.last_name} ({p.position || "N/A"})
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AbsenceFormContent({ displayedPlayers, isDarkMode, selectedPlayerId, setSelectedPlayerId, absenceType, setAbsenceType, startDate, setStartDate, endDate, setEndDate, createAbsence }) {
  return (
    <div className={`mt-5 rounded-2xl border p-4 shadow-sm ${isDarkMode ? "border-white/10 bg-gray-900/40" : "border-black/10 bg-white"}`}>
      <div className="font-semibold">Add an absence</div>
      <div className={`mt-1 text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
        Use this form to mark a player as unavailable.
      </div>

      <div className="mt-3 space-y-3">
        <div>
          <div className={`text-xs mb-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>Player</div>
          <PlayerDropdown
            displayedPlayers={displayedPlayers}
            selectedPlayerId={selectedPlayerId}
            setSelectedPlayerId={setSelectedPlayerId}
            isDarkMode={isDarkMode}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <div className={`text-xs mb-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>Type</div>
            <select
              value={absenceType}
              onChange={e => setAbsenceType(e.target.value)}
              className={`w-full rounded-xl border px-3 py-2.5 text-sm shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/60 ${
                isDarkMode ? "bg-gray-950 border-gray-700" : "bg-white border-gray-300"
              }`}
            >
<option value="douleur">Douleur</option>
<option value="musculaire">Musculaire</option>
<option value="articulaire">Articulaire</option>
<option value="other">Autre</option>
            </select>
          </div>
          <div>
            <div className={`text-xs mb-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>Start</div>
            <input
              type="date"
              value={startDate}
              onChange={e => {
                const v = e.target.value;
                setStartDate(v);
                if (!endDate || endDate < v) setEndDate(v);
              }}
              className={`w-full rounded-xl border px-3 py-2.5 text-sm shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/60 ${
                isDarkMode ? "bg-gray-800 border-gray-600 text-gray-100 [color-scheme:dark]" : "bg-white border-gray-300 text-gray-900 [color-scheme:light]"
              }`}
            />
          </div>
          <div>
            <div className={`text-xs mb-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>End</div>
            <input
              type="date"
              value={endDate}
              onChange={e => setEndDate(e.target.value)}
              className={`w-full rounded-xl border px-3 py-2.5 text-sm shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/60 ${
                isDarkMode ? "bg-gray-800 border-gray-600 text-gray-100 [color-scheme:dark]" : "bg-white border-gray-300 text-gray-900 [color-scheme:light]"
              }`}
            />
          </div>
        </div>

        <button
          onClick={createAbsence}
          disabled={!selectedPlayerId}
          className={`w-full rounded-xl px-3 py-2.5 text-sm font-semibold transition shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/60 ${
            !selectedPlayerId
              ? (isDarkMode ? "bg-gray-800 text-gray-400" : "bg-gray-200 text-gray-500")
              : (isDarkMode ? "bg-indigo-600 hover:bg-indigo-500 text-white" : "bg-indigo-600 hover:bg-indigo-700 text-white")
          }`}
        >
          Add absence
        </button>
      </div>
    </div>
  );
}



function ContractRow({ player, contractsByPlayerId, setContractsByPlayerId, isDarkMode, showToast, setHasUnsavedChanges }) {
  const [startDate, setStartDate] = useState(contractsByPlayerId[player.id]?.start_date?.slice(0, 10) || "");
  const [endDate, setEndDate] = useState(contractsByPlayerId[player.id]?.end_date?.slice(0, 10) || "");
  const [isDirty, setIsDirty] = useState(false);   // ← champ modifié ?
  const [isSaved, setIsSaved] = useState(false);   // ← vient d'être sauvegardé ?

  const inputCls = `rounded-lg border px-2 py-1 text-sm w-36 ${isDarkMode ? "bg-gray-800 border-gray-600 text-gray-100 [color-scheme:dark]" : "bg-white border-gray-300 [color-scheme:light]"}`;

  async function validate() {
    try {
      const res = await fetchAuth(`/contracts/${player.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ start_date: startDate, end_date: endDate }),
      });
      if (!res.ok) throw new Error();
      setContractsByPlayerId(prev => ({ ...prev, [player.id]: { ...prev[player.id], start_date: startDate, end_date: endDate } }));
      showToast("success", "Contract updated.");
      setHasUnsavedChanges(false);
      setIsDirty(false);
      setIsSaved(true);
      setTimeout(() => setIsSaved(false), 2000); // ← redevient invisible après 2s
    } catch { showToast("error", "Failed to update."); }
  }

  return (
    <tr className={isDarkMode ? "bg-gray-900" : "bg-white"}>
      <td className="px-3 py-2 border-b">{player.first_name}</td>
      <td className="px-3 py-2 border-b">{player.last_name}</td>
      <td className="px-3 py-2 border-b">{player.position}</td>
      <td className="px-3 py-2 border-b">
        <input type="date" value={startDate}
         //onChange={e => { setStartDate(e.target.value); setIsDirty(true); setIsSaved(false); setHasUnsavedChanges(true); }}
         onClick={(e) => e.preventDefault()}
         className={`${inputCls} opacity-80 cursor-default`} />
      </td>
      <td className="px-3 py-2 border-b">
        <input type="date" value={endDate}
         // onChange={e => { setEndDate(e.target.value); setIsDirty(true); setIsSaved(false); setHasUnsavedChanges(true); }}
          onClick={(e) => e.preventDefault()}
          className={`${inputCls} opacity-80 cursor-default`} />
      </td>
      <td className="px-3 py-2 border-b">
        {/* ✅ Visible seulement si modifié ou juste sauvegardé */}
        {(isDirty || isSaved) && (
          <button onClick={validate}
            className={`p-1.5 rounded-lg transition ${
              isSaved
                ? "bg-green-500/20 border border-green-400/30 text-green-400"           // ← vert après save
                : isDarkMode
                  ? "bg-indigo-500/20 border border-indigo-400/30 text-indigo-300 hover:bg-indigo-500/30"
                  : "bg-indigo-50 border border-indigo-200 text-indigo-700 hover:bg-indigo-100"
            }`}
            title="Validate">
            <Check className="h-4 w-4" />
          </button>
        )}
      </td>
    </tr>
  );
}

function ConfirmModal({ open, onClose, onConfirm, message, isDarkMode, confirmLabel = "Delete", confirmCls = "bg-red-600 hover:bg-red-700" }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className={`relative w-full max-w-md p-6 rounded-xl shadow-2xl ${isDarkMode ? "bg-gray-800 text-white" : "bg-white text-gray-900"}`}>
        <h2 className="text-lg font-semibold mb-4">Confirmation</h2>
        <p className="mb-6">{message}</p>
        <div className="flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-gray-400 hover:opacity-80">
            Cancel
          </button>
          {/* ✅ Un seul bouton dynamique */}
          <button onClick={onConfirm} className={`px-4 py-2 rounded-lg text-white ${confirmCls}`}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}




const MedicalImageryListPage = ({ onCardClick }) => {
  const { t } = useTranslation();
  const { isDarkMode } = useTheme();
  const { season } = useSeason();
  const { allPlayers, loading, error, isFallbackData, refetch } = usePlayersPerf();

  const hasPlayers = Array.isArray(allPlayers) && allPlayers.length > 0;
  const showMainLoader = loading && !hasPlayers;
  const isSoftLoading = loading && hasPlayers;

  // Track player enabled status changes
  const [playerEnabledChanges, setPlayerEnabledChanges] = useState({});
  const [toast, setToast] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const displayedPlayers = useMemo(() => {
    const baseList = Array.isArray(allPlayers) ? allPlayers : [];
    // Apply enabled status overrides
    return baseList.map(p => ({
      ...p,
      player_enabled: playerEnabledChanges[p.id] !== undefined ? playerEnabledChanges[p.id] : p.player_enabled
    }));
  }, [allPlayers, playerEnabledChanges]);

// Drawer state
// Drawer state

const [drawerOpen, setDrawerOpen] = useState(false);
const [filterType, setFilterType] = useState("contract");
const [filterPosition, setFilterPosition] = useState("All");
const [filterSeason, setFilterSeason] = useState("");
const [filterPlayerId, setFilterPlayerId] = useState("");
const [activePlayerId, setActivePlayerId] = useState(null);
const [activeFormType, setActiveFormType] = useState(null); // "contract" ou "absence"
const btnCls = "rounded-xl px-3 py-1.5 bg-indigo-600 text-white hover:bg-indigo-700 text-sm";
const [seasons, setSeasons] = useState([]);
const [allSeasonPlayers, setAllSeasonPlayers] = useState({});
const [seasonsLoaded, setSeasonsLoaded] = useState(false);
const [absences, setAbsences] = useState([]);
const [contractsByPlayerId, setContractsByPlayerId] = useState({});
const [editingAbsence, setEditingAbsence] = useState(null);
const [absenceToDelete, setAbsenceToDelete] = useState(null);
const [showUnsavedModal, setShowUnsavedModal] = useState(false);

useEffect(() => {
  if (!displayedPlayers.length) return;

  async function fetchAllContracts() {
    const results = {};
    await Promise.all(
      displayedPlayers.map(async (p) => {
        try {
          const encodedId = encodeId(p.id);
          const res = await fetchAuth(`/contracts/${encodedId}`);
          if (!res.ok) return;
          const data = await res.json();
          results[p.id] = data;
        } catch {
          // pas de contrat
        }
      })
    );
    setContractsByPlayerId(results);
  }

  fetchAllContracts();
}, [displayedPlayers]);

useEffect(() => {
  const handleBeforeUnload = (e) => {
    if (hasUnsavedChanges) {
      e.preventDefault();
      e.returnValue = ""; // Requis pour Chrome
    }
  };
  window.addEventListener("beforeunload", handleBeforeUnload);
  return () => window.removeEventListener("beforeunload", handleBeforeUnload);
}, [hasUnsavedChanges]);

const filteredPlayers = useMemo(() => {
  if (!seasonsLoaded) return [];

  let arr = filterSeason && allSeasonPlayers[filterSeason]
    ? allSeasonPlayers[filterSeason]
    : Array.isArray(displayedPlayers) ? displayedPlayers : [];

  if (filterPlayerId) arr = arr.filter(p => p.id === filterPlayerId);
  if (filterPosition !== "All") arr = arr.filter(p => p.position === filterPosition);
  if (filterType === "contract" && filterSeason) {
  const [startYear, endYear] = filterSeason.split("-");
  const seasonStart = `${startYear}-07-01`;
  const seasonEnd = `${endYear}-06-30`;

  arr = arr.filter(p => {
    const contract = contractsByPlayerId[p.id];
    if (!contract) return false; // pas de contrat = exclu
    const cStart = contract.start_date?.slice(0, 10);
    const cEnd = contract.end_date?.slice(0, 10);
    if (!cStart || !cEnd) return false;
    return cStart <= seasonEnd && cEnd >= seasonStart;
  });
}

  if (filterType === "absence") {
    // Filtrer les absences par saison si une saison est sélectionnée
    let relevantAbsences = absences;
    if (filterSeason) {
      // La saison est au format "2023-2024", on extrait les années
      const [startYear, endYear] = filterSeason.split("-");
      const seasonStart = `${startYear}-07-01`; 
      const seasonEnd = `${endYear}-06-30`;     

      relevantAbsences = absences.filter(a => {
        const absStart = a.start_date?.slice(0, 10);
        const absEnd = a.end_date?.slice(0, 10);
        // chevauchement : absence commence avant fin saison ET finit après début saison
        return absStart <= seasonEnd && absEnd >= seasonStart;
      });
    }

    const absentIds = new Set(relevantAbsences.map(a => a.player_id));
    arr = arr.filter(p => absentIds.has(p.id));
  }

  return arr;
}, [displayedPlayers, filterPlayerId, filterPosition, filterSeason, allSeasonPlayers, seasonsLoaded, filterType, absences,contractsByPlayerId]);
  const [activeOn, setActiveOn] = useState(isoToday());
  const [absencePosition, setAbsencePosition] = useState("All");


  const [absLoading, setAbsLoading] = useState(false);
  const [absError, setAbsError] = useState(null);

  // Form state
  const [selectedPlayerId, setSelectedPlayerId] = useState("");
  const [absenceType, setAbsenceType] = useState("other");
  const [startDate, setStartDate] = useState(isoToday());
  const [endDate, setEndDate] = useState(isoToday());

  async function fetchAbsences(dateStr = activeOn) {
    setAbsLoading(true);
    setAbsError(null);
    try {
      const res = await fetchAuth(`/absences?active_on=${encodeURIComponent(dateStr)}`);
      if (!res.ok) throw new Error(`Failed: ${res.status}`);
      const data = await res.json();
      setAbsences(Array.isArray(data) ? data : []);
    } catch (e) {
    
      setAbsError(e);
      setAbsences([]);
    } finally {
      setAbsLoading(false);
    }
  }
async function updateAbsence() {
  if (!editingAbsence) return;
  try {
    const res = await fetchAuth(`/absences/${editingAbsence.id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        absence_type: editingAbsence.absence_type,
        start_date: editingAbsence.start_date,
        end_date: editingAbsence.end_date,
      }),
    });
    if (!res.ok) throw new Error(`Update failed: ${res.status}`);
    await fetchAbsences(activeOn);
    setEditingAbsence(null);
    showToast("success", "Absence updated successfully.");
  } catch (e) {
    
    showToast("error", "Failed to update absence.");
  }
}
function showToast(type, message) {
  setToast({ type, message });
  setTimeout(() => setToast(null), 3500);
}

  
async function confirmDeleteAbsence() {
  if (!absenceToDelete) return;

  try {
    const absence = absences.find(a => a.id === absenceToDelete);
    const playerId = absence?.player_id;

    const res = await fetchAuth(`/absences/${absenceToDelete}`, {
      method: "DELETE",
    });
    if (!res.ok) throw new Error(`Delete failed: ${res.status}`);

    setAbsences((prev) => prev.filter((a) => a.id !== absenceToDelete));

    // ✅ Vérifier le contrat avant de réactiver
    if (playerId) {
      const encodedId = encodeId(playerId);
      const contractRes = await fetchAuth(`/contracts/${encodedId}`);
      
      if (contractRes.ok) {
        const contract = await contractRes.json();
        const today = isoToday();
        const contractEnd = contract.end_date?.slice(0, 10);

        if (contractEnd && contractEnd >= today) {
          // ✅ Contrat encore actif → réactiver le joueur
          setPlayerEnabledChanges(prev => ({ ...prev, [playerId]: true }));
          fetchAuth(`/players/${encodedId}/toggle_enabled`, {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ player_enabled: true, source: "absence" }),
          });
          showToast("success", "Absence deleted and player reactivated.");
        } else {
          // ❌ Contrat expiré → ne pas réactiver
          showToast("success", "Absence deleted. Player not reactivated (contract expired).");
        }
      } else {
        // Pas de contrat trouvé → ne pas réactiverff
        showToast("success", "Absence deleted. Player not reactivated (no active contract).");
      }
    }

  } catch (e) {
    
    showToast("error", "Failed to delete absence.");
  } finally {
    setAbsenceToDelete(null);
  }
}
async function createAbsence() {
  if (!selectedPlayerId) return;
  try {
    const payload = {
      player_id: selectedPlayerId,
      absence_type: absenceType,
      start_date: startDate || isoToday(),
      end_date: endDate || startDate || isoToday(),
    };

    const res = await fetchAuth(`/absences`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error();

    await fetchAbsences(activeOn);

    // ✅ Désactiver le joueur immédiatement
    const encodedId = encodeId(selectedPlayerId);
    setPlayerEnabledChanges(prev => ({ ...prev, [selectedPlayerId]: false }));
    fetchAuth(`/players/${encodedId}/toggle_enabled`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ player_enabled: false, source: "absence" }),
    });

    setSelectedPlayerId("");
    setAbsenceType("other");
    setStartDate(activeOn);
    setEndDate(activeOn);
    showToast("success", "Absence added and player deactivated.");
  } catch (e) {
    showToast("error", "Failed to create absence.");
  }
}



  async function handleToggleActive(playerId, newStatus) {
    // Update UI immediately (optimistic update)
    setPlayerEnabledChanges(prev => ({
      ...prev,
      [playerId]: newStatus
    }));
    
    // Call API to save to database
    // refetch() is automatically called inside togglePlayer
    const result = await togglePlayer(playerId, newStatus, refetch);
    
    if (result === null) {
      // Error occurred, undo the optimistic update
      setPlayerEnabledChanges(prev => {
        const updated = { ...prev };
        delete updated[playerId];
        return updated;
      });
    } else {
      // Success: Clear the local override since context is synced
      setPlayerEnabledChanges(prev => {
        const updated = { ...prev };
        delete updated[playerId];
        return updated;
      });
    }
  }

useEffect(() => {
  async function fetchSeasons() {
    try {
      const res = await fetchAuth("/seasons");
      if (!res.ok) throw new Error(`Failed to fetch seasons: ${res.status}`);
      const data = await res.json();
      // data.seasons = ["2023-2024", "2022-2023", ...]
      const seasonOptions = data.seasons.map((s, idx) => ({
        id: s,        // on peut utiliser le label comme ID si pas d'autre identifiant
        name: s
      }));
      setSeasons(seasonOptions);
    } catch (e) {
      
      setSeasons([]);
    }
  }

  fetchSeasons();
}, []);

useEffect(() => {
  if (!seasons.length) return;

  async function preloadAllSeasons() {
    const results = {};
    await Promise.all(
      seasons.map(async (s) => {
        try {
          const res = await fetchAuth(`/playersperfs?season=${s.id}`);
          if (!res.ok) return;
          const data = await res.json();
          results[s.id] = Array.isArray(data.players)
            ? data.players.map(p => normalizePlayer(p))
            : [];
        } catch {
          results[s.id] = [];
        }
      })
    );
    setAllSeasonPlayers(results);  // ← ici à l'intérieur
    setSeasonsLoaded(true);        // ← ici à l'intérieur
  }

  preloadAllSeasons();
}, [seasons]);


  // Fetch absences when drawer opens / date changes
  useEffect(() => {
    if (!drawerOpen) return;
    fetchAbsences(activeOn);
  }, [drawerOpen, activeOn]);

  // Optional: polling while open
  useEffect(() => {
    if (!drawerOpen) return;
    const id = setInterval(() => fetchAbsences(activeOn), 60 * 1000);
    return () => clearInterval(id);
  }, [drawerOpen, activeOn]);

  const absencesFiltered = useMemo(() => {
    const arr = Array.isArray(absences) ? absences : [];
    if (absencePosition === "All") return arr;
    return arr.filter((a) => a?.player?.position === absencePosition);
  }, [absences, absencePosition]);

  const drawerCls = isDarkMode
    ? "bg-gray-950 text-gray-100 border-l border-white/10"
    : "bg-white text-gray-900 border-l border-black/10";

  return (
    <div
      className={`min-h-screen p-6 space-y-6 transition-colors ${
        isDarkMode
          ? "bg-gradient-to-br from-gray-950 via-gray-900 to-gray-800 text-gray-100"
          : "bg-gradient-to-br from-gray-50 via-white to-gray-100 text-gray-900"
      }`}
    >
      <header className="space-y-2">
<div className="flex items-start justify-between gap-3">
  <div className="space-y-1">
      <h1 className={`text-3xl font-bold tracking-tight ${isDarkMode ? "text-white" : "text-gray-900"}`}>
        {t("pages.infos.title")}
      </h1>
    <p className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>
      {t("pages.infos.subtitle")}
    </p>
  </div>

  {/* Conteneur pour les boutons */}
<div className="flex gap-2">
  {/* Bouton existant pour absences */}
  <button
  onClick={() => setDrawerOpen(true)}
  className={[
    "inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border text-sm font-semibold shadow-sm transition",
    "focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/60 focus-visible:ring-offset-2",
    isDarkMode
      ? "bg-gradient-to-r from-indigo-600/30 to-slate-900 border-white/10 text-gray-50 hover:from-indigo-600/40 hover:to-slate-900"
      : "bg-gradient-to-r from-indigo-600 to-indigo-500 border-indigo-600/40 text-white hover:from-indigo-700 hover:to-indigo-600",
    isDarkMode ? "focus-visible:ring-offset-gray-900" : "focus-visible:ring-offset-white",
  ].join(" ")}
>
  <ClipboardList className="h-4 w-4 opacity-80" />
  {t("pages.infos.manage")}
</button>

</div>

</div>


        {error && !hasPlayers && (
          <div
            className={`mt-3 rounded-xl border px-3 py-2 text-sm ${
              isDarkMode
                ? "border-red-500/60 bg-red-500/10 text-red-200"
                : "border-red-400 bg-red-50 text-red-800"
            }`}
          >
            {t("pages.infos.load_error")}
          </div>
        )}

        {isFallbackData && hasPlayers && (
          <div
            className={`mt-3 rounded-xl border px-3 py-2 text-sm ${
              isDarkMode
                ? "border-amber-500/60 bg-amber-500/10 text-amber-200"
                : "border-amber-400 bg-amber-50 text-amber-800"
            }`}
          >
            {t("pages.infos.degraded")}
          </div>
        )}
      </header>

      {showMainLoader ? (
        <div className="py-10 flex items-center justify-center">
          <span
            className={`inline-block h-6 w-6 mr-2 rounded-full border-2 border-current border-t-transparent animate-spin ${
              isDarkMode ? "text-indigo-300" : "text-indigo-600"
            }`}
          />
          <span className={isDarkMode ? "text-gray-300" : "text-gray-600"}>{t("pages.infos.loading")}</span>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {displayedPlayers.map((player) => (
          <PlayerBasicCard
  key={player.id}
  player={player}
  onClick={() => onCardClick?.(encodeId(player.id))}
  onToggleActive={handleToggleActive}
/>

          ))}
        </div>
      )}

      {/* Drawer Disponibilités */}
{drawerOpen && (
  <div className="fixed inset-0 z-50">
    {/* Overlay */}
    <div className="absolute inset-0 bg-black/50 backdrop-blur-[1px]" onClick={() => {
  if (hasUnsavedChanges) {
    setShowUnsavedModal(true);
  } else {
    setDrawerOpen(false);
  }
}} />

    <aside className={`absolute right-0 top-0 h-full w-full max-w-4xl shadow-2xl p-5 overflow-y-auto ${drawerCls}`}>
      {/* Header */}
<div className="flex items-center justify-between gap-3 mb-4">
  <div className="flex items-center gap-3">
    <h3 className="text-xl font-semibold">{t("pages.infos.manage")}</h3>
    {filterType === "absence" && (
      <button
        onClick={() => {
          setActiveFormType("absence");
          setActivePlayerId(null);
          setSelectedPlayerId("");
          setAbsenceType("other");
          setStartDate(isoToday());
          setEndDate(isoToday());
        }}
        className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-xl text-sm font-semibold ${
          isDarkMode ? "bg-indigo-600 hover:bg-indigo-500 text-white" : "bg-indigo-600 hover:bg-indigo-700 text-white"
        }`}
      >
        + Add Absence
      </button>
    )}
  </div>
<button
  className={`p-2 rounded-lg ${isDarkMode ? "hover:bg-gray-800" : "hover:bg-gray-100"}`}
  onClick={() => {
    if (hasUnsavedChanges) {
      setShowUnsavedModal(true); // ← affiche le modal
    } else {
      setDrawerOpen(false);
    }
  }}
  aria-label="Close"
>
  <X className="h-5 w-5" />
</button>
</div>

      {/* Filters */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
<select
  value={filterType}
  onChange={e => {
    setFilterType(e.target.value);
    setActiveFormType(null);
    setActivePlayerId(null);
  }}
  className={`w-full rounded-xl border px-3 py-2.5 text-sm shadow-sm ${
    isDarkMode ? "bg-gray-950 border-gray-700 text-gray-100" : "bg-white border-gray-300 text-gray-900"
  }`}
>
  <option value="contract">Contract</option>
  <option value="absence">Absence</option>
</select>

<select
  value={filterPosition}
  onChange={e => setFilterPosition(e.target.value)}
  className={`w-full rounded-xl border px-3 py-2.5 text-sm shadow-sm ${
    isDarkMode ? "bg-gray-950 border-gray-700 text-gray-100" : "bg-white border-gray-300 text-gray-900"
  }`}
>
  <option value="All">All Positions</option>
  <option value="Defense">Defense</option>
  <option value="Midfield">Midfield</option>
  <option value="Forward">Forward</option>
  <option value="Lateral">Lateral</option>
</select>

<select
  value={filterSeason}
  onChange={e => setFilterSeason(e.target.value)}
  className={`w-full rounded-xl border px-3 py-2.5 text-sm shadow-sm ${
    isDarkMode ? "bg-gray-950 border-gray-700 text-gray-100" : "bg-white border-gray-300 text-gray-900"
  }`}
>
  <option value="">All Seasons</option>
  {seasons.map(s => (
    <option key={s.id} value={s.id}>{s.name}</option>
  ))}
</select>



<select
  value={filterPlayerId}
  onChange={e => setFilterPlayerId(e.target.value)}
  className={`w-full rounded-xl border px-3 py-2.5 text-sm shadow-sm ${
    isDarkMode ? "bg-gray-950 border-gray-700 text-gray-100" : "bg-white border-gray-300 text-gray-900"
  }`}
>
  <option value="">All Players</option>
  {displayedPlayers.map(p => (
    <option key={p.id} value={p.id}>{p.first_name} {p.last_name}</option>
  ))}
</select>

      </div>

      {/* Tableau */}
<table className="w-full border-collapse">
  <thead>
    <tr>
      <th className="px-3 py-2 text-left border-b">First Name</th>
      <th className="px-3 py-2 text-left border-b">Last Name</th>
      <th className="px-3 py-2 text-left border-b">Position</th>
      {filterType === "contract" ? (
        <>
          <th className="px-3 py-2 text-left border-b">Contract Start</th>
<th className="px-3 py-2 text-left border-b">Contract End</th>
          <th className="px-3 py-2 text-left border-b">Actions</th>
        </>
      ) : (
        <>
          <th className="px-3 py-2 text-left border-b">Start Date</th>
          <th className="px-3 py-2 text-left border-b">End Date</th>
          <th className="px-3 py-2 text-left border-b">Type</th>
          <th className="px-3 py-2 text-left border-b">Actions</th>
        </>
      )}
    </tr>
  </thead>
<tbody>
  {filterType === "contract" ? (
filteredPlayers.map(player => (
  <ContractRow
    key={player.id}
    player={player}
    contractsByPlayerId={contractsByPlayerId}
    setContractsByPlayerId={setContractsByPlayerId}
    isDarkMode={isDarkMode}
    showToast={showToast}
    setHasUnsavedChanges={setHasUnsavedChanges} // ← ajouter
  />
))
  ) : filteredPlayers.length === 0 ? (
    <tr>
      <td colSpan={8} className={`px-3 py-6 text-center text-sm ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
        No absences found
      </td>
    </tr>
  ) : (
filteredPlayers.flatMap(player =>
  absences
    .filter(a => a.player_id === player.id)
    .map(a => (
      <AbsenceRow
        key={a.id}
        a={a}
        player={player}
        isDarkMode={isDarkMode}
        fetchAbsences={fetchAbsences}
        activeOn={activeOn}
        showToast={showToast}
        deleteAbsence={(id) => setAbsenceToDelete(id)}
        setHasUnsavedChanges={setHasUnsavedChanges} // ← ajouter

      />
    ))
)
  )}
</tbody>

</table>

{/* Formulaire modale (Contract ou Absence) */}
{editingAbsence && (
  <div className={`mt-5 rounded-2xl border shadow-sm ${isDarkMode ? "border-white/10 bg-gray-900/40" : "border-black/10 bg-white"}`}>
    <div className="flex items-center justify-between px-4 pt-4 pb-2">
      <div className="font-semibold text-sm">Edit Absence</div>
      <button
        onClick={() => setEditingAbsence(null)}
        className={`p-1.5 rounded-lg transition ${isDarkMode ? "hover:bg-gray-800 text-gray-400 hover:text-gray-100" : "hover:bg-gray-100 text-gray-500 hover:text-gray-900"}`}
      >
        <X className="h-4 w-4" />
      </button>
    </div>

    <div className="px-4 pb-4 space-y-3">
      <div>
        <div className={`text-xs mb-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>Player</div>
        <div className={`px-3 py-2.5 rounded-xl border text-sm ${isDarkMode ? "bg-gray-800 border-gray-700 text-gray-300" : "bg-gray-50 border-gray-200 text-gray-600"}`}>
          {(() => {
            const p = displayedPlayers.find(p => p.id === editingAbsence.player_id);
            return p ? `${p.first_name} ${p.last_name} (${p.position || "N/A"})` : "Unknown";
          })()}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div>
          <div className={`text-xs mb-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>Type</div>
          <select
            value={editingAbsence.absence_type}
            onChange={e => setEditingAbsence(prev => ({ ...prev, absence_type: e.target.value }))}
            className={`w-full rounded-xl border px-3 py-2.5 text-sm shadow-sm ${isDarkMode ? "bg-gray-950 border-gray-700 text-gray-100" : "bg-white border-gray-300"}`}
          >
           <option value="douleur">Douleur</option>
<option value="musculaire">Musculaire</option>
<option value="articulaire">Articulaire</option>
<option value="other">Autre</option>
          </select>
        </div>
        <div>
          <div className={`text-xs mb-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>Start</div>
          <input
            type="date"
            value={editingAbsence.start_date}
            onChange={e => {
              const v = e.target.value;
              setEditingAbsence(prev => ({ ...prev, start_date: v }));
            }}
            onBlur={() => {}} // empêche le blur involontaire
            className={`w-full rounded-xl border px-3 py-2.5 text-sm shadow-sm ${isDarkMode ? "bg-gray-800 border-gray-600 text-gray-100 [color-scheme:dark]" : "bg-white border-gray-300 text-gray-900 [color-scheme:light]"}`}
          />
        </div>
        <div>
          <div className={`text-xs mb-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>End</div>
          <input
            type="date"
            value={editingAbsence.end_date}
            onChange={e => {
              const v = e.target.value;
              setEditingAbsence(prev => ({ ...prev, end_date: v }));
            }}
            onBlur={() => {}}
            className={`w-full rounded-xl border px-3 py-2.5 text-sm shadow-sm ${isDarkMode ? "bg-gray-800 border-gray-600 text-gray-100 [color-scheme:dark]" : "bg-white border-gray-300 text-gray-900 [color-scheme:light]"}`}
          />
        </div>
      </div>

      <button
        onClick={updateAbsence}
        className={`w-full rounded-xl px-3 py-2.5 text-sm font-semibold transition shadow-sm ${isDarkMode ? "bg-green-600 hover:bg-green-500 text-white" : "bg-green-600 hover:bg-green-700 text-white"}`}
      >
        Save Changes
      </button>
    </div>
  </div>
)}
{(activeFormType === "contract" || activeFormType === "absence") && (
  <div className={`mt-5 rounded-2xl border shadow-sm ${isDarkMode ? "border-white/10" : "border-black/10"}`}>
    {/* Header avec X */}
    <div className={`flex items-center justify-between px-4 pt-4 pb-2`}>
      <div className="font-semibold text-sm">
        {activeFormType === "contract" ? "Edit Contract" : "Add Absence"}
      </div>
      <button
        onClick={() => {
          setActiveFormType(null);
          setActivePlayerId(null);
        }}
        className={`p-1.5 rounded-lg transition ${isDarkMode ? "hover:bg-gray-800 text-gray-400 hover:text-gray-100" : "hover:bg-gray-100 text-gray-500 hover:text-gray-900"}`}
        aria-label="Close form"
      >
        <X className="h-4 w-4" />
      </button>
    </div>

    {/* Contenu */}
{activeFormType === "contract" && (
  <ContractDrawerContent
    displayedPlayers={displayedPlayers}
    activePlayerId={activePlayerId}
    isDarkMode={isDarkMode}
    contractsByPlayerId={contractsByPlayerId}         // ← Ajouté
    setContractsByPlayerId={setContractsByPlayerId}   // ← Ajouté
    showToast={showToast}
  />
)}

{activeFormType === "absence" && (
  <AbsenceFormContent
    displayedPlayers={activePlayerId ? displayedPlayers.filter(p => p.id === activePlayerId) : displayedPlayers}
    isDarkMode={isDarkMode}
    selectedPlayerId={selectedPlayerId}
    setSelectedPlayerId={setSelectedPlayerId}
    absenceType={absenceType}
    setAbsenceType={setAbsenceType}
    startDate={startDate}
    setStartDate={setStartDate}
    endDate={endDate}
    setEndDate={setEndDate}
    createAbsence={createAbsence}
  />
)}
  </div>
)}
    </aside>
  </div>
)}



      {/* Loading Modal */}
      {isSoftLoading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center ptr-none">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-[1px]" />
          <div className={`relative rounded-2xl p-6 shadow-2xl flex items-center gap-3 ${isDarkMode ? "bg-gray-900 text-gray-100" : "bg-white text-gray-900"}`}>
            <span
              className={`h-5 w-5 rounded-full border-2 border-current border-t-transparent animate-spin ${
                isDarkMode ? "text-indigo-300" : "text-indigo-600"
              }`}
            />
            <span className="font-medium">Refreshing players data…</span>
          </div>
        </div>
      )}
      {/* Toast notification */}
{toast && (
  <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[100] animate-in fade-in slide-in-from-bottom-4">
    <div className={`flex items-center gap-3 px-5 py-3.5 rounded-2xl shadow-2xl border text-sm font-medium backdrop-blur-sm transition-all ${
      toast.type === "success"
        ? isDarkMode
          ? "bg-green-500/20 border-green-400/30 text-green-200"
          : "bg-green-50 border-green-300 text-green-800"
        : isDarkMode
          ? "bg-red-500/20 border-red-400/30 text-red-200"
          : "bg-red-50 border-red-300 text-red-800"
    }`}>
      {toast.type === "success" ? (
        <svg className="h-5 w-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ) : (
        <svg className="h-5 w-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      )}
      {toast.message}
    </div>
  </div>
)}
<ConfirmModal
  open={!!absenceToDelete}
  onClose={() => setAbsenceToDelete(null)}
  onConfirm={confirmDeleteAbsence}
  message="Are you sure you want to delete this absence?"
  isDarkMode={isDarkMode}
/>
<ConfirmModal
  open={showUnsavedModal}
  onClose={() => setShowUnsavedModal(false)}
  onConfirm={() => {
    setHasUnsavedChanges(false);
    setShowUnsavedModal(false);
    setDrawerOpen(false);
  }}
  message="You have unsaved changes. Are you sure you want to leave without saving?"
  confirmLabel="Leave without saving"
  confirmCls="bg-red-600 hover:bg-red-700"
  isDarkMode={isDarkMode}
/>
    </div>
  );
};

export default MedicalImageryListPage;
